import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  Divider,
  Breadcrumbs,
  Link,
  LinearProgress,
} from '@weave-mui/material';
import { FormControlLabel } from '@mui/material';
import FormLabel, { formLabelVariants } from '@weave-mui/form-label';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';

import LoadingSpinner from 'components/LoadingSpinner';
import PageTitle from 'components/PageTitle';
import WeaveMuiThemeProvider from 'components/WeaveMuiThemeProvider';
import { addNewNotification } from 'store/actions/notification_actions';
import {
  getHostingProject,
  getHostingProjectTestCases,
  getHostingProjectTestCase,
  postHostingNewTestCase,
  putHostingEditTestCase,
} from 'utils/api.service';
import { formatSubmissionErrorDetail } from 'utils/formatSubmissionError';

const HIDDEN_FIELDS = ['env', 'testdata'];
const PARAM_HELPER = 'Click on add to save a parameter';

/**
 * Build Yup validation schema from metafields
 * @param {Array} metafields - Metafield definitions from API
 * @returns {Yup.ObjectSchema}
 */
function buildValidationSchema(metafields) {
  const shape = {};

  (metafields || []).forEach((field) => {
    if (!field.field || HIDDEN_FIELDS.includes(field.field)) return;

    let schema = Yup.mixed();

    if (field.type === 'string' || field.type === 'number') {
      schema = Yup.string().trim();
      if (
        field.mandatory !== 'false' &&
        field.field !== 'description' &&
        field.field !== 'spec' &&
        field.field !== 'grep' &&
        field.field !== 'wiki' &&
        field.field !== 'testcasename'
      ) {
        if (field.field === 'displayname') {
          schema = schema.required(`${field.name || field.field} is required`);
        }
      }
      if (field.field === 'displayname') {
        schema = schema.max(200, 'Display name must be 200 characters or less');
      }
      if (field.field === 'testcasename') {
        schema = schema.max(
          100,
          'Test case name must be 100 characters or less',
        );
      }
    }

    if (field.type === 'text') {
      schema = Yup.string().trim();
    }

    if (field.field === 'parameters') {
      schema = Yup.object().nullable().default({});
    }

    if (field.field === 'notsupportedenv') {
      schema = Yup.array().nullable().default([]);
    }

    shape[field.field] = schema;
  });

  // Ensure required base fields
  if (!shape.displayname) {
    shape.displayname = Yup.string()
      .trim()
      .required('Display name is required')
      .max(200, 'Display name must be 200 characters or less');
  }
  return Yup.object().shape(shape);
}

/**
 * TestCaseForm - Full-page form for adding or editing a test case.
 * Matches Figma: Add a test / Edit test case (breadcrumbs, two-column layout,
 * parameters key-value + Add, disable testcase for checkboxes, Cancel / Add test case | Update test case).
 *
 * @param {Object} props - Component props
 * @param {'create' | 'edit'} [props.mode='create'] - Form mode
 * @returns {JSX.Element}
 */
function TestCaseForm({ mode = 'create' }) {
  const history = useHistory();
  const location = useLocation();
  const {
    category,
    projectname: projectnameParam,
    testcasename: testcasenameParam,
  } = useParams();
  const dispatch = useDispatch();

  const menu = useSelector((state) => state.app.menu);
  const service = menu.service;
  const workstream = menu[service];
  const workstreamName = menu.categoryName || workstream;
  const org = service;

  const isEditMode = mode === 'edit';

  // Initialize from location.state if available
  const initialProject = location.state?.initialProject || null;
  const initialMetafields = location.state?.initialMetafields || [];
  const initialTestCase = location.state?.initialTestCase || null;

  const [isLoading, setIsLoading] = useState(!initialProject);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [metafields, setMetafields] = useState(initialMetafields);
  const [project, setProject] = useState(initialProject);
  const [testCaseToEdit, setTestCaseToEdit] = useState(initialTestCase);
  const [paramKey, setParamKey] = useState('');
  const [paramValue, setParamValue] = useState('');

  const supportedEnvs = useMemo(() => {
    if (!project?.envlist) return [];
    return (project.envlist || []).map(
      (e) => e.displayname?.toUpperCase?.() || e.displayname || e,
    );
  }, [project]);

  /**
   * Fetch project, metafields and (in edit mode) test case
   */
  useEffect(() => {
    if (!org || !workstream || !projectnameParam) {
      return;
    }

    // If we already have the data from navigation state, don't re-fetch
    if (project && metafields.length > 0 && (!isEditMode || testCaseToEdit)) {
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch project and metafields if missing
        if (!project || metafields.length === 0) {
          const [projectRes, testCasesRes] = await Promise.all([
            getHostingProject(org, workstream, projectnameParam),
            getHostingProjectTestCases(org, workstream, projectnameParam),
          ]);
          setProject(projectRes.data);
          setMetafields(testCasesRes.data.metafield || []);
        }

        // Fetch test case if in edit mode and missing
        if (isEditMode && testcasenameParam && !testCaseToEdit) {
          const tcRes = await getHostingProjectTestCase(
            org,
            workstream,
            projectnameParam,
            testcasenameParam,
          );
          setTestCaseToEdit(tcRes.data);
        }
      } catch (error) {
        console.error('Error loading test case form data:', error);
        dispatch(
          addNewNotification(
            error.response?.data?.detail || 'Failed to load form data',
            'danger',
          ),
        );
        history.push(`/hosting/${category}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    org,
    workstream,
    projectnameParam,
    testcasenameParam,
    isEditMode,
    category,
    history,
    dispatch,
    project,
    metafields,
    testCaseToEdit,
  ]);

  const validationSchema = useMemo(
    () => buildValidationSchema(metafields),
    [metafields],
  );

  const initialValues = useMemo(() => {
    const values = {
      notsupportedenv: [],
      parameters: {},
    };

    (metafields || []).forEach((field) => {
      if (HIDDEN_FIELDS.includes(field.field)) return;
      if (field.field === 'parameters') {
        values[field.field] =
          typeof testCaseToEdit?.parameters === 'object' &&
          testCaseToEdit.parameters !== null
            ? { ...testCaseToEdit.parameters }
            : {};
        return;
      }
      if (field.field === 'notsupportedenv') {
        const envList = testCaseToEdit?.notsupportedenv;
        values[field.field] = Array.isArray(envList) ? [...envList] : [];
        return;
      }
      const fromTc = testCaseToEdit?.[field.field];
      if (fromTc !== undefined && fromTc !== null) {
        values[field.field] = fromTc;
      } else if (field.default !== undefined) {
        values[field.field] = field.default;
      } else {
        values[field.field] = '';
      }
    });

    if (isEditMode && testCaseToEdit) {
      values.displayname = testCaseToEdit.displayname ?? '';
      values.description = testCaseToEdit.description ?? '';
      values.testcasename = testCaseToEdit.testcasename ?? '';
      values.spec = testCaseToEdit.spec ?? '';
      values.grep = testCaseToEdit.grep ?? '';
      values.wiki = testCaseToEdit.wiki ?? '';
      values.parameters =
        typeof testCaseToEdit.parameters === 'object' &&
        testCaseToEdit.parameters !== null
          ? { ...testCaseToEdit.parameters }
          : {};
      values.notsupportedenv = Array.isArray(testCaseToEdit.notsupportedenv)
        ? [...testCaseToEdit.notsupportedenv]
        : [];
    }

    return values;
  }, [metafields, testCaseToEdit, isEditMode]);

  const handleCancel = useCallback(() => {
    history.push(`/hosting/${category}`);
  }, [history, category, projectnameParam]);

  const handleSubmit = useCallback(
    async (values) => {
      setIsSubmitting(true);
      try {
        const notsupportedenv = Array.isArray(values.notsupportedenv)
          ? values.notsupportedenv
          : [];
        const parameters =
          values.parameters && typeof values.parameters === 'object'
            ? values.parameters
            : {};

        const fieldNames = (metafields || [])
          .filter((f) => f.field && !HIDDEN_FIELDS.includes(f.field))
          .map((f) => f.field);

        const payload = {
          projectname: projectnameParam,
          displayname: values.displayname ?? '',
          description: values.description ?? '',
          testcasename: isEditMode
            ? testCaseToEdit?.testcasename
            : (values.testcasename ?? ''),
          spec: values.spec ?? '',
          grep: values.grep ?? '',
          wiki: values.wiki ?? '',
          parameters,
          notsupportedenv,
        };

        fieldNames.forEach((key) => {
          if (key === 'parameters' || key === 'notsupportedenv') return;
          if (payload[key] !== undefined) return;
          const v = values[key];
          if (v !== undefined && v !== null && v !== '') {
            payload[key] = v;
          }
        });

        // For compatibility: include testdata field, default to {} if not present.
        if (payload.testdata === undefined || payload.testdata === null) {
          payload.testdata =
            isEditMode &&
            testCaseToEdit?.testdata != null &&
            typeof testCaseToEdit.testdata === 'object'
              ? testCaseToEdit.testdata
              : {};
        }

        // Set default values for testdata fields as testdata_ENV: [{_tags: [], _name: '', ...}]
        // when adding or editing a test case (testdata is hidden in this form).
        if (project?.envlist && Array.isArray(project.envlist)) {
          const envlist = project.envlist.map((env) => env.env);
          envlist.forEach((env) => {
            const testDataEnvKey = `testdata_${env}`.toLowerCase();
            const element = isEditMode ? testCaseToEdit : {};
            const isOldTestDataFormat = !element[testDataEnvKey];
            if (isOldTestDataFormat) {
              const oldData =
                element.testdata && typeof element.testdata === 'object'
                  ? element.testdata[env]
                  : undefined;
              payload[testDataEnvKey] = [
                {
                  _tags: [],
                  _name: '',
                  ...(oldData && typeof oldData === 'object' ? oldData : {}),
                },
              ];
            } else {
              payload[testDataEnvKey] = element[testDataEnvKey];
            }
          });
        }

        if (isEditMode && testCaseToEdit) {
          payload.testcasename = testCaseToEdit.testcasename;
          await putHostingEditTestCase(
            workstream,
            projectnameParam,
            testCaseToEdit.testcasename,
            payload,
          );
          dispatch(
            addNewNotification('Test case updated successfully', 'success'),
          );
        } else {
          const isMaven = project?.tools === 'maven';
          if (isMaven) delete payload.testcasename;
          const testcases = [payload];
          await postHostingNewTestCase({
            workstreamname: workstream,
            projectname: projectnameParam,
            testcases,
          });
          dispatch(
            addNewNotification('Test case created successfully', 'success'),
          );
        }

        history.push(`/hosting/${category}`, {
          refreshProject: projectnameParam,
        });
      } catch (error) {
        const detail = error.response?.data?.detail;
        const message =
          formatSubmissionErrorDetail(detail) ??
          error.response?.data?.message ??
          (isEditMode
            ? 'Failed to update test case'
            : 'Failed to create test case');
        dispatch(addNewNotification(message, 'danger'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isEditMode,
      testCaseToEdit,
      projectnameParam,
      workstream,
      category,
      history,
      dispatch,
      project?.tools,
      project?.envlist,
      metafields,
    ],
  );

  /** For testcasename, strip backend "(required)" from label. */
  const getFieldDisplayLabel = useCallback((field) => {
    const raw = field.name || field.field;
    if (field.field === 'testcasename') {
      return raw.replace(/\s*\(?\s*unique\s*\)?\s*/gi, ' ').trim();
    }
    return raw;
  }, []);

  const renderFieldLabel = useCallback(
    (field) => {
      const optional = field.mandatory === 'false';
      const unique = field.field === 'testcasename';
      const label = getFieldDisplayLabel(field);
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 0.5,
          minHeight: 24,
          '& label.MuiFormLabel-root': {
            margin: 0,
            marginLeft: '0 !important',
            padding: 0,
            paddingLeft: '0 !important',
            lineHeight: 1.5,
            alignItems: 'center',
          },
        }}
      >
        <FormLabel htmlFor={field.field} variant={formLabelVariants.TOP}>
          {label}
        </FormLabel>
        {optional && (
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1.5 }}
          >
            (optional)
          </Typography>
        )}
        {unique && (
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1.5 }}
          >
            (unique)
          </Typography>
        )}
        {field.description && (
          <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}
            title={field.description}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </Box>
        )}
      </Box>
    );
  }, [getFieldDisplayLabel]);

  const renderInput = useCallback(
    (field, formikProps) => {
      const { values, setFieldValue } = formikProps;
      const value = values[field.field];
      const readOnly = isEditMode && field.field === 'testcasename';
      const hidden =
        HIDDEN_FIELDS.includes(field.field) ||
        (field.field === 'testcasename' &&
          project?.tools === 'maven' &&
          !isEditMode);
      if (hidden) return null;

      if (field.field === 'parameters') {
        const params = typeof value === 'object' && value !== null ? value : {};
        const paramEntries = Object.entries(params);

        const canAddParameter =
          paramKey.trim() !== '' && paramValue.trim() !== '';

        const handleAddParameter = () => {
          const trimmedKey = paramKey.trim();
          const trimmedValue = paramValue.trim();
          if (trimmedKey !== '' && trimmedValue !== '') {
            setFieldValue('parameters', {
              ...params,
              [trimmedKey]: trimmedValue,
            });
            setParamKey('');
            setParamValue('');
          }
        };

        const handleRemoveParameter = (keyToRemove) => {
          const next = { ...params };
          delete next[keyToRemove];
          setFieldValue('parameters', next);
        };

        return (
          <Box key={field.field}>
            {renderFieldLabel(field)}
            <FormControl fullWidth variant="outlined">
              <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                <TextField
                  id="parameters-key"
                  value={paramKey}
                  onChange={(e) => setParamKey(e.target.value)}
                  placeholder="Key"
                  variant="outlined"
                  fullWidth
                  size="small"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddParameter();
                    }
                  }}
                />
                <TextField
                  value={paramValue}
                  onChange={(e) => setParamValue(e.target.value)}
                  placeholder="Value"
                  variant="outlined"
                  fullWidth
                  size="small"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddParameter();
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddParameter}
                  disabled={!canAddParameter}
                  sx={{ minWidth: 80 }}
                >
                  Add
                </Button>
              </Box>
              <FormHelperText
                sx={{
                  marginTop: '4px !important',
                  marginBottom: '8px !important',
                  fontSize: '12px',
                  color: 'text.secondary',
                  fontWeight: 400,
                }}
              >
                {PARAM_HELPER}
              </FormHelperText>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {paramEntries.map(([k, v]) => (
                  <Chip
                    key={k}
                    label={`${k}: ${String(v)}`}
                    onDelete={() => handleRemoveParameter(k)}
                    size="small"
                    deleteIcon={<CloseIcon sx={{ width: 24, height: 24 }} />}
                    sx={{
                      minWidth: 70,
                      height: 24,
                      fontSize: '12px',
                      fontWeight: 600,
                      borderRadius: '4px',
                    }}
                  />
                ))}
              </Box>
            </FormControl>
          </Box>
        );
      }

      if (field.field === 'notsupportedenv') {
        const selected = Array.isArray(value) ? value : [];
        return (
          <Box key={field.field}>
            {renderFieldLabel(field)}
            <FormControl component="fieldset" fullWidth>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {supportedEnvs.map((env) => (
                  <FormControlLabel
                    key={env}
                    sx={{ marginLeft: 0 }}
                    control={
                      <Checkbox
                        checked={selected.includes(env)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...selected, env]
                            : selected.filter((x) => x !== env);
                          setFieldValue('notsupportedenv', next);
                        }}
                        name={`notsupportedenv-${env}`}
                      />
                    }
                    label={env}
                  />
                ))}
              </Box>
            </FormControl>
          </Box>
        );
      }

      if (field.type === 'text') {
        return (
          <Box key={field.field}>
            {renderFieldLabel(field)}
            <Field name={field.field}>
              {({ field: f, meta }) => (
                <TextField
                  {...f}
                  fullWidth
                  multiline
                  minRows={2}
                  variant="outlined"
                  size="small"
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error}
                  placeholder={
                    field.default ||
                    `Enter ${getFieldDisplayLabel(field)}`
                  }
                  disabled={readOnly}
                />
              )}
            </Field>
          </Box>
        );
      }

      if (field.type === 'select' && field.options) {
        const options = Array.isArray(field.options) ? field.options : [];
        return (
          <Box key={field.field}>
            {renderFieldLabel(field)}
            <Field name={field.field}>
              {({ field: f, meta }) => (
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  error={meta.touched && Boolean(meta.error)}
                >
                  <Select
                    {...f}
                    id={field.field}
                    displayEmpty
                    renderValue={(v) => v || ''}
                    disabled={readOnly}
                    sx={{
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                      },
                    }}
                    variant="outlined"
                  >
                    {options
                      .filter(
                        (opt) =>
                          (typeof opt === 'string' ? opt : opt.id ?? opt.value ?? opt) !=
                            null &&
                          (typeof opt === 'string' ? opt : opt.id ?? opt.value ?? opt) !==
                            ''
                      )
                      .map((opt) => {
                        const optValue =
                          typeof opt === 'string'
                            ? opt
                            : (opt.id ?? opt.value ?? opt);
                        const optLabel =
                          typeof opt === 'string'
                            ? opt
                            : (opt.name ?? opt.label ?? optValue);
                        return (
                          <MenuItem key={optValue} value={optValue}>
                            {optLabel}
                          </MenuItem>
                        );
                      })}
                  </Select>
                  {meta.touched && meta.error && (
                    <FormHelperText>{meta.error}</FormHelperText>
                  )}
                </FormControl>
              )}
            </Field>
          </Box>
        );
      }

      if (
        field.type === 'string' ||
        field.type === 'number' ||
        field.type === 'date' ||
        !field.type
      ) {
        return (
          <Box key={field.field}>
            {renderFieldLabel(field)}
            <Field name={field.field}>
              {({ field: f, meta }) => (
                <TextField
                  {...f}
                  fullWidth
                  variant="outlined"
                  size="small"
                  type={field.field === 'wiki' ? 'url' : 'text'}
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error}
                  placeholder={field.default || 'Value'}
                  disabled={readOnly}
                />
              )}
            </Field>
          </Box>
        );
      }

      return null;
    },
    [
      isEditMode,
      getFieldDisplayLabel,
      project?.tools,
      renderFieldLabel,
      supportedEnvs,
      paramKey,
      paramValue,
    ],
  );

  /** Visible metafields: all fields from API except hidden (env, testdata). Hide testcasename when project tools is maven (add mode only; show readonly when editing). */
  const visibleMetafields = useMemo(
    () =>
      (metafields || []).filter((f) => {
        if (!f.field || HIDDEN_FIELDS.includes(f.field)) return false;
        if (
          f.field === 'testcasename' &&
          project?.tools === 'maven' &&
          !isEditMode
        )
          return false;
        return true;
      }),
    [metafields, project?.tools, isEditMode],
  );

  /** Left column: even index (0, 2, 4, ...); right column: odd index (1, 3, 5, ...) */
  const leftFields = useMemo(
    () => visibleMetafields.filter((_, i) => i % 2 === 0),
    [visibleMetafields],
  );
  const rightFields = useMemo(
    () => visibleMetafields.filter((_, i) => i % 2 === 1),
    [visibleMetafields],
  );

  if (isLoading) {
    return (
      <WeaveMuiThemeProvider>
        <Box sx={{ p: 3 }}>
          <LoadingSpinner text="Loading test case form..." />
        </Box>
      </WeaveMuiThemeProvider>
    );
  }

  if (!project) {
    return null;
  }

  const pageTitle = isEditMode ? 'Edit test case' : 'Add a test';
  const submitLabel = isEditMode ? 'Update test case' : 'Add test case';

  return (
    <WeaveMuiThemeProvider>
      <Box sx={{ p: 3, maxWidth: 1200 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }} separator="/">
          <Link
            underline="hover"
            color="text.secondary"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              history.push('/');
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Home
            </Typography>
          </Link>
          <Link
            underline="hover"
            color="text.secondary"
            href={`/hosting/${category}`}
            onClick={(e) => {
              e.preventDefault();
              history.push(`/hosting/${category}`);
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Test hosting
            </Typography>
          </Link>
          <Link
            underline="hover"
            color="text.secondary"
            href={`/hosting/${category}`}
            onClick={(e) => {
              e.preventDefault();
              history.push(`/hosting/${category}`);
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {workstreamName}
            </Typography>
          </Link>
          <Typography variant="body2" color="text.primary">
            {pageTitle}
          </Typography>
        </Breadcrumbs>

        <PageTitle title={pageTitle} />

        {isSubmitting && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress />
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={isEditMode}
        >
          {(formikProps) => (
            <Form>
              <Box
                sx={{
                  maxHeight: 'calc(100vh - 320px)',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  pr: 1,
                  mr: -1,
                }}
              >
                <Grid container spacing={3} alignItems="flex-start">
                  <Grid item xs={12} md={6}>
                    <Box sx={{ '& > *:not(:last-child)': { mb: 3 } }}>
                      {leftFields.map((field) =>
                        renderInput(field, formikProps),
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ '& > *:not(:last-child)': { mb: 3 } }}>
                      {rightFields.map((field) =>
                        renderInput(field, formikProps),
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  py: 2.5,
                  px: 3,
                  mx: -3,
                  mb: -3,
                  mt: 3,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  paddingTop: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? isEditMode
                      ? 'Updating...'
                      : 'Adding...'
                    : submitLabel}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </WeaveMuiThemeProvider>
  );
}

export default TestCaseForm;
